const apiPaths = {
  rewrite: '/ai/rewrite',
};

export function useAIApi() {
  const api = useLocalApi();

  return {
    rewrite({
      text,
      context,
      metadata,
      signal,
      onMeta,
      onDelta,
      onDone,
      onError,
    }) {
      const controller = new AbortController();

      if (signal) {
        if (signal.aborted) {
          controller.abort();
        } else {
          signal.addEventListener('abort', () => controller.abort(), {once: true});
        }
      }

      const url = api.getFullUrl({path: apiPaths.rewrite});
      const headers = {
        'Content-Type': 'application/json'
      };

      const done = (async () => {
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({text, context, metadata}),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error('Rewrite failed');
        }

        if (!response.body) {
          const data = await response.json();
          if (data?.rewritten) {
            onDelta?.(data.rewritten);
            onDone?.({reason: 'stop'});
          }
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let streamError = null;
        let doneReceived = false;

        const processLine = (line) => {
          if (!line.trim()) return;

          let parsed;
          try {
            parsed = JSON.parse(line);
          } catch (err) {
            return;
          }

          switch (parsed.type) {
            case 'meta':
              onMeta?.(parsed);
              break;
            case 'delta':
              if (parsed.text) {
                onDelta?.(parsed.text);
              }
              break;
            case 'done':
              doneReceived = true;
              onDone?.({reason: parsed.reason});
              break;
            case 'error':
              streamError = new Error(parsed.message || 'Rewrite failed');
              onError?.(streamError, parsed);
              break;
          }
        };

        while (true) {
          const {value, done} = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, {stream: true});
          const lines = buffer.split('\n');
          buffer = lines.pop();
          lines.forEach(processLine);

          if (streamError) {
            break;
          }
        }

        if (buffer) {
          processLine(buffer);
        }

        if (streamError) {
          throw streamError;
        }

        if (!doneReceived) {
          onDone?.({reason: 'stop'});
        }
      })();

      return {
        cancel() {
          controller.abort();
        },
        done,
      };
    },
  };
}
